import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, AlertTriangle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import './Features.css';

const features = [
  {
    id: 'design-studio',
    title: 'AI-Powered 3D Design Studio',
    pain: 'Architects charge ₹50,000-₹2,00,000 for designs. You can\'t visualize the house before building. Changes are expensive.',
    desc: 'Design your entire house in real-time 3D by chatting with your personal AI architect - just type or speak, as easy as WhatsApp. Describe what you want, and the AI builds it for you. See exactly what your home will look like - before spending a single rupee on construction.',
    bullets: ['AI architect via text or voice input', 'Live 3D visualization', 'Virtual walkthrough mode', 'Blueprint to 3D conversion', '100+ ready templates', 'Conversational design experience', 'Furniture & fixture placement'],
    img: '/screenshots/design-studio.png',
  },
  {
    id: 'cost-engine',
    title: 'Real-Time Cost Estimation Engine',
    pain: 'Builders give vague estimates. Hidden costs appear mid-construction. Budgets overrun by 30-40%.',
    desc: 'Every change you make instantly updates your total cost with full itemization - materials, labor, timeline. Know the exact cost of every room, every wall, every fixture. No surprises.',
    bullets: ['Instant cost updates', 'Material-level itemization', 'Labor cost breakdown', 'Timeline estimation', 'Regional price accuracy', 'Bill of quantities export', 'Cost comparison tools'],
    img: '/screenshots/Realtime-cost-estimator.PNG',
  },
  {
    id: 'partner-matching',
    title: 'Verified Construction Partners',
    pain: 'Finding a good contractor is a gamble. Cheap contractors cut corners. Expensive ones aren\'t always better.',
    desc: 'Sculpit removes contractor selection entirely. We auto-assign the best verified partner for your project. All partners are RERA registered with ISO quality certification.',
    bullets: ['Auto-matched to your project', 'RERA registered builders', 'ISO quality certified', '35+ years combined experience', 'Background verified', 'Performance-tracked', 'Sculpit quality guarantee'],
    img: '/screenshots/verified-construction-partners.png',
  },
  {
    id: 'project-tracking',
    title: 'Real-Time Project Tracking',
    pain: 'You have no idea what\'s happening at your construction site. Contractors give vague updates. Problems are discovered too late.',
    desc: 'Track every milestone from your phone. Get predictive delay alerts 3-5 days before they happen. See photos, inspection reports, and progress updates in real-time.',
    bullets: ['Live milestone dashboard', 'Photo & video updates', 'Predictive delay alerts', '15+ quality inspections', 'Inspection reports', 'GPS-tracked site visits', 'Weather delay monitoring'],
    img: '/screenshots/realtime-tracking.png',
  },
  {
    id: 'escrow',
    title: 'Escrow-Protected Payments',
    pain: 'You pay large amounts upfront. Contractor slows down or disappears. Your money is gone, house is half-built.',
    desc: 'Your money goes into a Sculpit escrow account. Released only when each milestone is verified through quality inspections. No bulk upfront payments. No abandonment risk.',
    bullets: ['Milestone-based release', 'Independent verification', 'No upfront bulk payments', 'Transparent disbursement', 'Bank-grade security', 'Real-time balance tracking', 'Dispute resolution'],
    img: '/screenshots/escrow-payments.jpg',
  },
  {
    id: 'home-loans',
    title: 'Home Loan Integration',
    pain: 'Getting a construction loan takes months. Banks don\'t trust individual builders. Documentation is a nightmare.',
    desc: 'Pre-loan affordability analysis, lender-ready documentation, pre-verified builder credentials. We cut approval timelines from months to weeks.',
    bullets: ['Affordability calculator', 'Lender-ready documents', 'Pre-verified credentials', 'Multiple bank options', 'EMI planning', 'Escrow integration', 'Fast-track approvals'],
    img: '/screenshots/loans.png',
  },
  {
    id: 'mep',
    title: 'MEP Planning with AI Clash Detection',
    pain: 'Electrical and plumbing layouts conflict. Problems discovered after walls are built. Expensive rework.',
    desc: 'AI plans your electrical, plumbing, and HVAC layouts. Automatically detects clashes between systems before construction starts, saving lakhs in rework costs.',
    bullets: ['Electrical layout planning', 'Plumbing layout planning', 'HVAC system design', 'AI clash detection', 'Automated routing', 'Code compliance', 'Material estimation'],
    img: '/screenshots/MEP-Clash-detection.png',
  },
  {
    id: 'vastu',
    title: 'Vastu Compliance Checker',
    pain: 'Vastu consultants charge separately. Compliance issues discovered after design is finalized. Rework needed.',
    desc: 'Built-in Vastu compliance analysis checks your design against traditional Vastu principles. Get suggestions and fix issues before construction starts.',
    bullets: ['Automatic Vastu analysis', 'Room placement guidance', 'Direction-based suggestions', 'Compliance scoring', 'Fix suggestions', 'Expert consultation option', 'Regional Vastu variants'],
    img: '/screenshots/vaastu-compliance-checker.png',
  },
];

const competitors = [
  { feature: 'AI 3D Design Studio', sculpit: true, brickbolt: false, buildnext: false, livspace: false },
  { feature: 'Instant Cost Estimation', sculpit: true, brickbolt: false, buildnext: true, livspace: false },
  { feature: 'Real-Time 3D Visualization', sculpit: true, brickbolt: false, buildnext: false, livspace: false },
  { feature: 'Construction Execution', sculpit: true, brickbolt: true, buildnext: true, livspace: false },
  { feature: 'Escrow Payments', sculpit: true, brickbolt: false, buildnext: false, livspace: false },
  { feature: 'MEP Planning', sculpit: true, brickbolt: false, buildnext: false, livspace: false },
  { feature: 'Virtual Walkthroughs', sculpit: true, brickbolt: false, buildnext: false, livspace: true },
  { feature: 'Home Loan Integration', sculpit: true, brickbolt: true, buildnext: false, livspace: false },
  { feature: 'Interior Design', sculpit: true, brickbolt: false, buildnext: false, livspace: true },
  { feature: 'Quality Inspections', sculpit: true, brickbolt: true, buildnext: true, livspace: false },
];

export default function Features() {
  return (
    <div className="features-page">
      <section className="features-hero section-dark">
        <div className="features-hero__bg">
          <div className="features-hero__orb features-hero__orb--1" />
          <div className="features-hero__orb features-hero__orb--2" />
        </div>
        <div className="container features-hero__content">
          <AnimatedSection>
            <span className="label label-teal">Everything You Need</span>
            <h1>Features Built for <span className="text-gradient">Real Problems</span></h1>
            <p className="features-hero__subtitle">
              Every feature solves a specific pain point that Indian homeowners face. Nothing here is fluff - everything has a purpose.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {features.map((feature, i) => (
        <section key={i} id={feature.id} className={`section ${i % 2 !== 0 ? 'section-gray' : ''}`}>
          <div className="container">
            <div className={`feature-detail ${i % 2 !== 0 ? 'feature-detail--reverse' : ''}`}>
              <AnimatedSection direction={i % 2 === 0 ? 'left' : 'right'}>
                <div className="feature-detail__content">
                  <div className="feature-detail__pain">
                    <span className="feature-detail__pain-label"><AlertTriangle size={14} /> The Problem</span>
                    <p>{feature.pain}</p>
                  </div>
                  <h2>{feature.title}</h2>
                  <p className="feature-detail__desc">{feature.desc}</p>
                  <ul className="feature-detail__bullets">
                    {feature.bullets.map((b, j) => (
                      <li key={j}><Check size={18} className="feature-check" /> {b}</li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
              <AnimatedSection direction={i % 2 === 0 ? 'right' : 'left'}>
                <div className="feature-detail__visual">
                  <div className="feature-detail__img glass-card">
                    <img src={feature.img} alt={feature.title} />
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      ))}

      {/* Competitor Comparison */}
      <section className="section section-dark">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Only Sculpit</span>
              <h2>No One Else Does It All</h2>
              <p>
                AI 3D Design + Instant Pricing + Construction Execution - all in one platform. No competitor offers this complete solution.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="competitor-table-wrap">
              <table className="competitor-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="competitor-table__sculpit">Sculpit</th>
                    <th>Brick&Bolt</th>
                    <th>BuildNext</th>
                    <th>Livspace</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((row, i) => (
                    <tr key={i}>
                      <td>{row.feature}</td>
                      <td className="competitor-table__sculpit">{row.sculpit ? <Check size={18} className="comp-yes" /> : <X size={18} className="comp-no" />}</td>
                      <td>{row.brickbolt ? <Check size={18} className="comp-yes-dim" /> : <X size={18} className="comp-no" />}</td>
                      <td>{row.buildnext ? <Check size={18} className="comp-yes-dim" /> : <X size={18} className="comp-no" />}</td>
                      <td>{row.livspace ? <Check size={18} className="comp-yes-dim" /> : <X size={18} className="comp-no" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section" style={{ textAlign: 'center' }}>
        <div className="container">
          <AnimatedSection>
            <h2>See All Features in Action</h2>
            <p className="cta-subtitle">
              Start designing for free and explore every feature yourself.
            </p>
            <Link to="/pricing" className="btn btn-primary btn-lg">
              Start Designing Free <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
